import React, { useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Download,
  Printer,
  FileSpreadsheet,
} from 'lucide-react';

function formatRM(n) {
  const v = Number(n) || 0;
  return `RM ${v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function csvEscape(val) {
  return `"${String(val ?? '').replace(/"/g, '""')}"`;
}

function downloadCsv(filename, headerRow, dataRows) {
  const lines = [headerRow.map(csvEscape).join(',')];
  dataRows.forEach((row) => lines.push(row.map(csvEscape).join(',')));
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function BarChart({ title, rows, valueKey = 'amount', labelKey = 'name', color = '#2563eb' }) {
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  if (!rows.length) {
    return (
      <div className="jet-finance-chart-card">
        <h4>{title}</h4>
        <p className="jet-finance-empty">No data for this period.</p>
      </div>
    );
  }
  return (
    <div className="jet-finance-chart-card">
      <h4>{title}</h4>
      <div className="jet-finance-bars">
        {rows.map((row) => {
          const val = Number(row[valueKey]) || 0;
          const pct = (val / max) * 100;
          return (
            <div key={row[labelKey]} className="jet-finance-bar-row">
              <span className="jet-finance-bar-label" title={row[labelKey]}>{row[labelKey]}</span>
              <div className="jet-finance-bar-track">
                <div className="jet-finance-bar-fill" style={{ width: `${pct}%`, background: row.color || color }} />
              </div>
              <span className="jet-finance-bar-val">{formatRM(val)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DonutChart({ title, segments }) {
  const total = segments.reduce((s, x) => s + (Number(x.value) || 0), 0) || 1;
  let acc = 0;
  const parts = segments
    .filter((s) => Number(s.value) > 0)
    .map((seg) => {
      const pct = (Number(seg.value) / total) * 100;
      const start = acc;
      acc += pct;
      return `${seg.color} ${start}% ${acc}%`;
    });

  return (
    <div className="jet-finance-chart-card">
      <h4>{title}</h4>
      <div className="jet-finance-donut-layout">
        <div
          className="jet-finance-donut"
          style={{ background: parts.length ? `conic-gradient(${parts.join(', ')})` : '#e2e8f0' }}
        >
          <div className="jet-finance-donut-hole">
            <strong>{formatRM(total)}</strong>
            <span>Total</span>
          </div>
        </div>
        <div className="jet-finance-donut-legend">
          {segments.filter((s) => Number(s.value) > 0).map((seg) => (
            <div key={seg.label} className="jet-finance-donut-legend-row">
              <span className="jet-finance-donut-dot" style={{ background: seg.color }} />
              <span className="jet-finance-donut-legend-label">{seg.label}</span>
              <span className="jet-finance-donut-legend-val">{formatRM(seg.value)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FlowChart({ title, points }) {
  if (!points.length) {
    return (
      <div className="jet-finance-chart-card jet-finance-chart-card--wide">
        <h4>{title}</h4>
        <p className="jet-finance-empty">No transactions in this period.</p>
      </div>
    );
  }

  const maxVal = Math.max(...points.map((p) => Math.max(p.income, p.expense)), 1);
  const w = 480;
  const h = 160;
  const padX = 28;
  const padY = 16;
  const step = points.length > 1 ? (w - padX * 2) / (points.length - 1) : 0;

  const incomePts = points.map((p, i) => {
    const x = padX + i * step;
    const y = h - padY - (p.income / maxVal) * (h - padY * 2);
    return `${x},${y}`;
  }).join(' ');

  const expensePts = points.map((p, i) => {
    const x = padX + i * step;
    const y = h - padY - (p.expense / maxVal) * (h - padY * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="jet-finance-chart-card jet-finance-chart-card--wide">
      <h4>
        {title}
        <span className="jet-finance-chart-legend">
          <span className="jet-finance-legend-line income" /> Income
          <span className="jet-finance-legend-line expense" /> Expenses
        </span>
      </h4>
      <svg viewBox={`0 0 ${w} ${h}`} className="jet-finance-flow-svg" preserveAspectRatio="none">
        <line x1={padX} y1={h - padY} x2={w - padX} y2={h - padY} stroke="#cbd5e1" strokeWidth="1" />
        <polyline fill="none" stroke="#059669" strokeWidth="2.5" strokeLinejoin="round" points={incomePts} />
        <polyline fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinejoin="round" strokeDasharray="5 4" points={expensePts} />
        {points.map((p, i) => {
          const x = padX + i * step;
          const yInc = h - padY - (p.income / maxVal) * (h - padY * 2);
          const yExp = h - padY - (p.expense / maxVal) * (h - padY * 2);
          return (
            <g key={p.key}>
              <circle cx={x} cy={yInc} r="3.5" fill="#059669" />
              <circle cx={x} cy={yExp} r="3.5" fill="#dc2626" />
            </g>
          );
        })}
      </svg>
      <div className="jet-finance-flow-labels">
        {points.map((p) => (
          <span key={p.key}>{p.label}</span>
        ))}
      </div>
    </div>
  );
}

export default function FinanceOverview({ summary, incomes, expenses, dateFrom, dateTo }) {
  const collectionTotal = Number(summary?.collectionIncome?.total) || 0;
  const manualTotal = Number(summary?.manualIncome?.total) || 0;
  const expenseTotal = Number(summary?.expenses?.total) || 0;
  const paymentsTotal = Number(summary?.paymentsReceived?.total) || 0;
  const netBalance = Number(summary?.netBalance) || 0;

  const expenseBars = useMemo(() => {
    const rows = (summary?.expenses?.byCategory || []).slice(0, 8);
    const colors = ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#e11d48', '#f97316', '#fb7185', '#f43f5e'];
    return rows.map((r, i) => ({ ...r, color: colors[i % colors.length] }));
  }, [summary]);

  const incomeBars = useMemo(() => {
    const manual = (summary?.manualIncome?.byCategory || []).map((r) => ({
      name: r.name,
      amount: r.amount,
      color: '#2563eb',
    }));
    const rows = [
      { name: 'Collection income (auto)', amount: collectionTotal, color: '#059669' },
      ...manual,
    ].filter((r) => Number(r.amount) > 0);
    return rows.slice(0, 8);
  }, [summary, collectionTotal]);

  const donutSegments = useMemo(() => [
    { label: 'Collection income', value: collectionTotal, color: '#059669' },
    { label: 'Recorded income', value: manualTotal, color: '#2563eb' },
    { label: 'Expenses', value: expenseTotal, color: '#dc2626' },
  ], [collectionTotal, manualTotal, expenseTotal]);

  const flowPoints = useMemo(() => {
    const buckets = {};
    const add = (date, type, amount) => {
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 10);
      if (!buckets[key]) buckets[key] = { income: 0, expense: 0 };
      buckets[key][type] += Number(amount) || 0;
    };
    (incomes || []).forEach((t) => add(t.transactionDate, 'income', t.amount));
    (expenses || []).forEach((t) => add(t.transactionDate, 'expense', t.amount));
    (summary?.collectionIncome?.items || []).forEach((c) => add(c.date, 'income', c.amount));

    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => ({
        key,
        label: new Date(key).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        income: val.income,
        expense: val.expense,
      }));
  }, [incomes, expenses, summary]);

  const reportSlug = `${dateFrom}_to_${dateTo}`;

  const downloadSummaryReport = () => {
    downloadCsv(
      `finance-summary-${reportSlug}.csv`,
      ['Metric', 'Amount (RM)', 'Notes'],
      [
        ['Collection income', collectionTotal.toFixed(2), `${summary?.collectionIncome?.count || 0} collections`],
        ['Recorded income', manualTotal.toFixed(2), 'Manual entries'],
        ['Total income', (Number(summary?.totalIncome) || 0).toFixed(2), ''],
        ['Payments received', paymentsTotal.toFixed(2), 'Receipt payments'],
        ['Total expenses', expenseTotal.toFixed(2), ''],
        ['Net balance', netBalance.toFixed(2), `${dateFrom} to ${dateTo}`],
      ],
    );
  };

  const downloadIncomeReport = () => {
    const rows = (incomes || []).map((r) => [
      formatDate(r.transactionDate),
      r.country || '',
      r.categoryName || '',
      r.paymentMethod || '',
      r.description || '',
      r.reference || '',
      (Number(r.amount) || 0).toFixed(2),
    ]);
    (summary?.collectionIncome?.items || []).forEach((c) => {
      rows.push([
        formatDate(c.date),
        '',
        'Collection income (auto)',
        '',
        c.collectionNo || '',
        c.shipperName || '',
        (Number(c.amount) || 0).toFixed(2),
      ]);
    });
    downloadCsv(
      `finance-income-${reportSlug}.csv`,
      ['Date', 'Country', 'Category', 'Payment', 'Description', 'Reference', 'Amount (RM)'],
      rows,
    );
  };

  const downloadExpenseReport = () => {
    downloadCsv(
      `finance-expenses-${reportSlug}.csv`,
      ['Date', 'Country', 'Category', 'Description', 'Amount (RM)'],
      (expenses || []).map((r) => [
        formatDate(r.transactionDate),
        r.country || '',
        r.categoryName || '',
        r.description || '',
        (Number(r.amount) || 0).toFixed(2),
      ]),
    );
  };

  const printReport = () => {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    const expenseRows = (summary?.expenses?.byCategory || [])
      .map((r) => `<tr><td>${r.name}</td><td style="text-align:right">${formatRM(r.amount)}</td></tr>`)
      .join('');
    const incomeRows = incomeBars
      .map((r) => `<tr><td>${r.name}</td><td style="text-align:right">${formatRM(r.amount)}</td></tr>`)
      .join('');
    win.document.write(`<!DOCTYPE html><html><head><title>Finance Report ${dateFrom} – ${dateTo}</title>
      <style>
        body{font-family:system-ui,sans-serif;padding:2rem;color:#111}
        h1{margin:0 0 0.25rem} .sub{color:#666;margin-bottom:1.5rem}
        table{width:100%;border-collapse:collapse;margin:1rem 0}
        th,td{border:1px solid #ddd;padding:0.5rem 0.65rem;font-size:0.9rem}
        th{background:#f3f4f6;text-align:left}
        .kpis{display:grid;grid-template-columns:repeat(2,1fr);gap:0.75rem;margin-bottom:1.5rem}
        .kpi{border:1px solid #e5e7eb;border-radius:8px;padding:0.75rem}
        .kpi strong{display:block;font-size:1.25rem;margin-top:0.25rem}
      </style></head><body>
      <h1>Income & Expenses Report</h1>
      <p class="sub">${formatDate(dateFrom)} – ${formatDate(dateTo)} · Generated ${new Date().toLocaleString()}</p>
      <div class="kpis">
        <div class="kpi"><span>Collection income</span><strong>${formatRM(collectionTotal)}</strong></div>
        <div class="kpi"><span>Recorded income</span><strong>${formatRM(manualTotal)}</strong></div>
        <div class="kpi"><span>Total expenses</span><strong>${formatRM(expenseTotal)}</strong></div>
        <div class="kpi"><span>Net balance</span><strong>${formatRM(netBalance)}</strong></div>
      </div>
      <h2>Income breakdown</h2><table><thead><tr><th>Category</th><th>Amount</th></tr></thead><tbody>${incomeRows}</tbody></table>
      <h2>Expense breakdown</h2><table><thead><tr><th>Category</th><th>Amount</th></tr></thead><tbody>${expenseRows}</tbody></table>
      </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <div className="jet-finance-overview" id="finance-overview-print">
      <div className="jet-finance-kpis">
        <div className="jet-finance-kpi jet-finance-kpi--income">
          <TrendingUp className="w-5 h-5" />
          <div>
            <span>Collection income</span>
            <strong>{formatRM(collectionTotal)}</strong>
            <small>{summary?.collectionIncome?.count || 0} collections</small>
          </div>
        </div>
        <div className="jet-finance-kpi jet-finance-kpi--paid">
          <Wallet className="w-5 h-5" />
          <div>
            <span>Recorded income</span>
            <strong>{formatRM(manualTotal)}</strong>
            <small>Manual income entries</small>
          </div>
        </div>
        <div className="jet-finance-kpi jet-finance-kpi--expense">
          <TrendingDown className="w-5 h-5" />
          <div>
            <span>Total expenses</span>
            <strong>{formatRM(expenseTotal)}</strong>
            <small>Recorded expenses</small>
          </div>
        </div>
        <div className="jet-finance-kpi jet-finance-kpi--net">
          <Wallet className="w-5 h-5" />
          <div>
            <span>Net balance</span>
            <strong>{formatRM(netBalance)}</strong>
            <small>{formatDate(summary?.from)} – {formatDate(summary?.to)}</small>
          </div>
        </div>
      </div>

      <div className="jet-finance-report-bar">
        <span className="jet-finance-report-bar-label">Reports</span>
        <button type="button" className="jet-btn jet-btn-secondary" onClick={downloadSummaryReport}>
          <FileSpreadsheet className="w-4 h-4" />
          Summary CSV
        </button>
        <button type="button" className="jet-btn jet-btn-secondary" onClick={downloadIncomeReport}>
          <Download className="w-4 h-4" />
          Income CSV
        </button>
        <button type="button" className="jet-btn jet-btn-secondary" onClick={downloadExpenseReport}>
          <Download className="w-4 h-4" />
          Expenses CSV
        </button>
        <button type="button" className="jet-btn jet-btn-primary" onClick={printReport}>
          <Printer className="w-4 h-4" />
          Print report
        </button>
      </div>

      <div className="jet-finance-charts-grid">
        <DonutChart title="Income vs expenses" segments={donutSegments} />
        <BarChart title="Income sources" rows={incomeBars} color="#059669" />
        <BarChart title="Expenses by category" rows={expenseBars} color="#dc2626" />
      </div>

      <FlowChart title="Daily cash flow" points={flowPoints} />

      <div className="jet-finance-charts-grid jet-finance-charts-grid--2">
        <div className="jet-finance-chart-card">
          <h4>Payments received</h4>
          <div className="jet-finance-stat-hero">
            <strong>{formatRM(paymentsTotal)}</strong>
            <span>Receipt payments in period</span>
          </div>
          <div className="jet-finance-mini-compare">
            <div>
              <span>Total income</span>
              <strong>{formatRM(summary?.totalIncome)}</strong>
            </div>
            <div>
              <span>Net after expenses</span>
              <strong className={netBalance >= 0 ? 'pos' : 'neg'}>{formatRM(netBalance)}</strong>
            </div>
          </div>
        </div>
        <div className="jet-finance-chart-card">
          <h4>Top expenses</h4>
          {expenseBars.length ? (
            <ul className="jet-finance-rank-list">
              {expenseBars.slice(0, 5).map((row, i) => (
                <li key={row.name}>
                  <span className="jet-finance-rank">{i + 1}</span>
                  <span className="jet-finance-rank-name">{row.name}</span>
                  <span className="jet-finance-rank-val">{formatRM(row.amount)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="jet-finance-empty">No expenses in this period.</p>
          )}
        </div>
      </div>
    </div>
  );
}
